import type { AxiosInstance } from 'axios';
import qs from 'qs';
import { Tag } from '@/api/types/tag';
import { Customer, CustomerTag } from '@/api/types/customer';
import { TagModel } from '@/models/tag';
import { transformTags } from '@/api/transforms/tag';
import { CustomerModel, CustomerTagModel } from '@/models/customer';
import { transformCustomer, transformCustomerTag } from '@/api/transforms/customer';

interface AddNoteParams {
  readonly note: string;
  readonly customerId: string;
  readonly tagId: string;
}

class CustomerService {
  private client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getTagsForNotes(): Promise<TagModel[]> {
    const queryParams = qs.stringify({
      '$or': [{
        type: 'classification',
        isSignificant: 1
      }, {
        type: 'experience',
      }]
    });

    const response = await this.client.get<Tag[]>(`/tags?${queryParams}`);

    return transformTags(response.data.data);
  }

  async addCustomerTag({ note, customerId, tagId }: AddNoteParams): Promise<CustomerTagModel> {
    const response = await this.client.post<CustomerTag>('/customer-tags', {
      customerId,
      tagId,

      customerNote: {
        customerId,
        content: note,
      }
    }, {
      params: {
        '$expand': [
          'tag',
          'customerNote.createdByUser',
          'customerNote.customerNoteTags'
        ]
      }
    }, true);

    return transformCustomerTag(response.data);
  }

  async getCustomer(customerId: string): Promise<CustomerModel> {
    const response = await this.client.get<Customer>(`/customers/${customerId}`, {
      params: {
        '$expand': [
          'customerAddresses',
          'customerDocuments',
          'customerNotes',
          'customerNotes.createdByUser',
          'customerNotes.updatedByUser',
          'customerNotes.customerNoteTags',
          'customerNotes.customerNoteTags.tag',
          'customerTags',
          'classifications',
          'customerTags.tag'
        ]
      }
    }, true);

    return transformCustomer(response.data);
  }

  async getCustomersWithReservationsByContact(contact: string, contactType: string, apiKey?: string): Promise<CustomerModel[]> {
    const response = await this.client.get<Customer[]>('/customers', {
      params: {
        $query: {
          [contactType]: contact,
          mergedToCustomerId: null,
          $expand: {
            customerTags: {
              $expand: {
                tag: {}
              }
            },
            customerOfReservations: {
              status: {
                $ne: 'canceled',
              },
              endDate: {
                $gte: new Date().toISOString(),
              },
              $expand: {
                reservationNotes: {},
                company: {},
                propertyUnit: {
                  $expand: {
                    property: {},
                    propertyUnitCategory: {},
                  },
                },
              },
            },
          }
        }
      },
      headers: apiKey ? { 'x-api-key': apiKey } : undefined,
    });

    return response.data.data.map(transformCustomer);
  }
}

export { CustomerService };
